import { Component, OnInit, Pipe, PipeTransform, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FooterHeaderComponent } from '../footer-header/footer-header.component';
import { FooterComponent } from "../footer/footer.component";



interface PdfDocument {
  id: string;
  title: string;
  filename: string;
  description: string;
  icon: string;
  color: string;
}

@Pipe({
  name: 'safe',
  standalone: true
})
export class SafePipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}

@Component({
  selector: 'app-link-pages',
  standalone: true,
  imports: [CommonModule, SafePipe, FooterHeaderComponent, FooterComponent],
  templateUrl: './link-pages.component.html',
  styleUrls: ['./link-pages.component.scss']
})
export class LinkPagesComponent implements OnInit, AfterViewInit {
  isStandalonePage = false;
  
  pdfDocuments: PdfDocument[] = [
    {
      id: 'cookie-policy',
      title: 'Cookie Policy',
      filename: 'HPS LIfe Cookie Policy .pdf',
      description: 'Learn about how we use cookies and similar technologies on our website.',
      icon: '🍪',
      color: '#FF6B6B'
    },
    {
      id: 'modern-slavery',
      title: 'Modern Slavery Statement',
      filename: 'Modern Slavery Statement July 2025.pdf',
      description: 'Our commitment to preventing modern slavery and human trafficking.',
      icon: '🤝',
      color: '#4ECDC4'
    },
    {
      id: 'privacy-policy',
      title: 'Privacy Policy',
      filename: 'Privacy Policy July 2025.pdf',
      description: 'How we collect, use, and protect your personal information.',
      icon: '🔒',
      color: '#45B7D1'
    }
  ];

  constructor(private router: Router) { }

  ngOnInit(): void {
    // Consider it standalone if not on home route
    this.isStandalonePage = !this.router.url.startsWith('/home');
  }

  ngAfterViewInit(): void {
    // Initialize PDF containers after view is ready
    setTimeout(() => {
      this.pdfDocuments.forEach(pdfDoc => {
        this.setupPdfContainer(pdfDoc.id);
      });
    }, 500);
  }

  getPdfUrl(filename: string, page: number = 1): string {
    return `assets/docs/${filename}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&page=${page}`;
  }

  openPdf(pdfDoc: PdfDocument): void {
    const pdfPath = `assets/docs/${pdfDoc.filename}`;
    window.open(pdfPath, '_blank');
  }

  setupPdfContainer(documentId: string): void {
    const container = document.getElementById(`pdf-container-${documentId}`);
    if (container) {
      // Clear existing content
      container.innerHTML = '';
      
      // Create multiple page previews to simulate scrolling
      const pdfDoc = this.pdfDocuments.find(doc => doc.id === documentId);
      if (pdfDoc) {
        // Create 3 page previews stacked vertically
        for (let page = 1; page <= 3; page++) {
          const pageContainer = document.createElement('div');
          pageContainer.className = 'pdf-page-container';
          pageContainer.style.marginBottom = '10px';
          pageContainer.style.height = '300px';
          
          const iframe = document.createElement('iframe');
          iframe.src = this.getPdfUrl(pdfDoc.filename, page);
          iframe.style.width = '100%';
          iframe.style.height = '100%';
          iframe.style.border = 'none';
          iframe.style.borderRadius = '8px';
          iframe.setAttribute('frameborder', '0');
          iframe.setAttribute('scrolling', 'no');
          
          pageContainer.appendChild(iframe);
          container.appendChild(pageContainer);
        }
      }
      
      // Set up the container for scrolling
      container.style.overflowY = 'auto';
      container.style.maxHeight = '400px';
      container.style.padding = '10px';
      container.style.borderRadius = '12px';
      container.style.backgroundColor = '#f8f9fa';
    }
  }

  startScroll(documentId: string): void {
    const container = document.getElementById(`pdf-container-${documentId}`);
    if (container) {
      // Hide overlay on hover
      const overlay = document.querySelector(`#${documentId} .pdf-overlay`) as HTMLElement;
      if (overlay) {
        overlay.style.opacity = '0';
      }
      
      // Start scroll immediately with setInterval for instant response
      const startScroll = 0;
      const endScroll = container.scrollHeight - container.clientHeight;
      const totalSteps = 60; // 60 steps for smooth animation
      const stepDuration = 16; // ~60fps (16ms per step)
      let currentStep = 0;
      
      const scrollInterval = setInterval(() => {
        currentStep++;
        const progress = currentStep / totalSteps;
        
        // Smooth easing function
        const easeInOutQuart = (t: number): number => {
          return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t;
        };
        
        const easedProgress = easeInOutQuart(progress);
        const currentScroll = startScroll + (endScroll - startScroll) * easedProgress;
        
        container.scrollTop = currentScroll;
        
        if (currentStep >= totalSteps) {
          clearInterval(scrollInterval);
        }
      }, stepDuration);
      
      // Store interval for cleanup
      (container as any).scrollInterval = scrollInterval;
    }
  }

  stopScroll(documentId: string): void {
    const container = document.getElementById(`pdf-container-${documentId}`);
    if (container) {
      // Clear any ongoing scroll animation
      if ((container as any).scrollInterval) {
        clearInterval((container as any).scrollInterval);
      }
      
      // Reset to top with smooth animation
      container.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      
      // Show overlay again
      const overlay = document.querySelector(`#${documentId} .pdf-overlay`) as HTMLElement;
      if (overlay) {
        overlay.style.opacity = '1';
      }
    }
  }
}
